import {
  consumptionDurationHours,
  decAdd,
  formatDec,
  isDecimalText,
  type VoyageReportKind,
} from '@felix/domain';
import { ActionIcon, Alert, Button, Checkbox, Select, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconExternalLink,
  IconFileText,
  IconFlame,
  IconPencil,
  IconPlus,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { followLink, useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { useReportLabels } from '../voyage/report-labels.js';
import { UNLINKED } from '../voyage/screen-load.js';
import { DateTime } from './BunkeringPanel.js';
import cx from './Consumption.module.css';
import classes from './Ledger.module.css';
import { issuesOf, mt, Notices, Section, useLedgerError } from './ledger-ui.js';
import type { FuelSource } from './source.js';
import type {
  FuelConsumptionDetail,
  FuelConsumptionItem,
  FuelConsumptionLine,
  FuelConsumptionListData,
  FuelConsumptionSection,
  FuelConsumptionSplit,
  FuelContextData,
} from './types.js';

const OPERATIONS = [
  'sea',
  'manoeuvring',
  'port',
  'anchor',
  'drift',
  'canal_strait',
  'waiting',
] as const;
const METHODS = ['flowmeter', 'tank_level', 'bunker_delivery', 'calculated', 'estimated'] as const;
const QUALITIES = ['measured', 'calculated', 'estimated'] as const;
const UNITS = ['mt', 'm3', 'l', 'kg'] as const;
const SPLIT_REASONS = [
  'year',
  'port_arrival',
  'fuel_change',
  'regulatory_boundary',
  'company_change',
] as const;

export interface ConsumptionPanelProps {
  source: FuelSource;
  vesselId: string | null;
  onVessel?: (data: FuelConsumptionListData) => void;
}

/** UTC an ↔ gün ve saat (ekran UTC gösterir; ortak belgesi §9 "UTC zorunlu"). */
export function utcParts(iso: string | null): { day: string | null; time: string | null } {
  if (!iso) return { day: null, time: null };
  return { day: iso.slice(0, 10), time: iso.slice(11, 16) };
}
export function utcOf(day: string | null, time: string | null): string | null {
  if (!day || !time) return null;
  const at = Date.parse(`${day}T${time.slice(0, 5)}:00.000Z`);
  return Number.isNaN(at) ? null : new Date(at).toISOString();
}

const blankMeter = () => ({ start: null, end: null, unit: 'mt', deviceId: null, reset: null });

export function emptyLine(key: string): FuelConsumptionLine {
  return {
    key,
    equipmentId: null,
    fuelTypeId: null,
    tankId: null,
    batchId: null,
    method: 'flowmeter',
    quality: 'measured',
    massMt: null,
    meter: null,
    runningHours: null,
    density15: null,
    tempC: null,
    basis: null,
    remarks: null,
  };
}

/** Satırların toplamı (MT); geçersiz miktar sayılmaz. */
export function linesTotal(lines: readonly FuelConsumptionLine[]): string {
  return formatDec(
    lines.reduce(
      (sum, line) =>
        line.massMt !== null && isDecimalText(line.massMt) ? decAdd(sum, line.massMt) : sum,
      decAdd('0', '0'),
    ),
  );
}

/** Kaydedilecek bölüm: boş metinler `null`, boş sayaç atılır. */
export function sectionInput(section: FuelConsumptionSection): FuelConsumptionSection {
  const text = (value: string | null) => {
    const trimmed = (value ?? '').trim();
    return trimmed === '' ? null : trimmed;
  };
  return {
    periodStart: section.periodStart,
    operation: section.operation,
    lines: section.lines.map((line) => {
      const meter = line.meter;
      const keepMeter =
        meter !== null &&
        (text(meter.start) !== null || text(meter.end) !== null || meter.reset !== null);
      return {
        ...line,
        massMt: text(line.massMt),
        runningHours: text(line.runningHours),
        density15: text(line.density15),
        tempC: text(line.tempC),
        basis: text(line.basis),
        remarks: text(line.remarks),
        meter: keepMeter
          ? {
              start: text(meter.start),
              end: text(meter.end),
              unit: meter.unit,
              deviceId: text(meter.deviceId),
              reset: meter.reset
                ? { at: meter.reset.at, rolloverAt: text(meter.reset.rolloverAt) }
                : null,
            }
          : null,
      };
    }),
    splits:
      section.splits && section.splits.length > 0
        ? section.splits.map((split) => ({
            ...split,
            basis: text(split.basis),
            lines: split.lines.map((part) => ({ ...part, massMt: text(part.massMt) })),
          }))
        : null,
  };
}

/** Raporun formu (Noon kendi ekranında, diğerleri Leg / Event'te; ADR 0082). */
export function sourceReportPath(item: FuelConsumptionItem, vesselId: string | null): string {
  const query = new URLSearchParams();
  if (vesselId) query.set('vessel', vesselId);
  query.set('voyage', item.voyageId ?? UNLINKED);
  query.set('report', item.reportId);
  return `${item.kind === 'noon' ? '/voyage/noon-report' : '/voyage/legs'}?${query.toString()}`;
}

/** Açılacak rapor: bağlantıdaki, yoksa açık (taslak ya da yeniden açılmış) son rapor, yoksa en yeni. */
export function initialReport(items: readonly FuelConsumptionItem[], wanted: string | null) {
  if (wanted && items.some((i) => i.reportId === wanted)) return wanted;
  return (
    items.find((i) => i.status === 'draft' || i.status === 'reopened')?.reportId ??
    items[0]?.reportId ??
    null
  );
}

/**
 * Tüketim (ortağın 05-tuketim.jpeg; ADR 0117 §2, Ek Y3): tüketim ayrı bir kayıt değil, sefer
 * raporunun bölümüdür. Üstte Kaynak Raporu bandı ve rapor seçici; Tüketim Bilgileri (UTC dönem,
 * süre, operasyon, sefer); Tüketim Detayları tablosu (ekipman, yakıt, kaynak tank, çalışma saati,
 * yöntem, MT, veri niteliği) ve seçili satırın sayaç ayrıntısı; "Satır Ekle", "Taslak Kaydet"
 * (raporun taslağı), "Onaya Gönder" (raporun gönderimi; rapor kesinleşince defter). Ofiste salt
 * okunur.
 */
export function ConsumptionPanel({ source, vesselId, onVessel }: ConsumptionPanelProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const labels = useReportLabels();
  const errorText = useLedgerError();
  const [list, setList] = useState<FuelConsumptionListData | null>(null);
  const [context, setContext] = useState<FuelContextData | null>(null);
  const [detail, setDetail] = useState<FuelConsumptionDetail | null>(null);
  const [values, setValues] = useState<FuelConsumptionSection | null>(null);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState(0);
  const [open, setOpen] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'submit' | null>(null);
  const wanted = new URLSearchParams(shell?.search ?? '').get('report');

  const show = useCallback((next: FuelConsumptionDetail | null) => {
    setDetail(next);
    setDirty(false);
    setSelected(0);
    setIssues({});
    setValues(
      next
        ? (next.consumption ?? {
            periodStart: next.suggestedPeriodStart,
            operation: null,
            lines: [],
            splits: null,
          })
        : null,
    );
  }, []);

  const load = useCallback(
    async (reportId?: string | null) => {
      if (!source.consumptions || !source.consumption) {
        setFailed('GENERIC');
        return;
      }
      try {
        const [nextList, ctx] = await Promise.all([
          source.consumptions(vesselId),
          source.context(vesselId),
        ]);
        setList(nextList);
        setContext(ctx);
        onVessel?.(nextList);
        const id = reportId !== undefined ? reportId : initialReport(nextList.items, wanted);
        show(id ? await source.consumption(vesselId, id) : null);
        setFailed(null);
      } catch (failure) {
        setFailed(errorCode(failure));
      }
    },
    [source, vesselId, onVessel, wanted, show],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const fuelCode = useCallback(
    (id: string | null) => context?.fuelTypes.find((f) => f.id === id)?.code ?? '—',
    [context],
  );
  const lines = values?.lines ?? [];
  const total = useMemo(() => linesTotal(lines), [lines]);

  if (failed) {
    return (
      <LoadError code={failed} onRetry={() => void load()} title={t('fuel.tabs.consumption')} />
    );
  }
  if (!list || !context) {
    return <TableSkeleton columns={6} rows={6} label={t('fuel.tabs.consumption')} />;
  }

  const c = (key: string, options: Record<string, unknown> = {}) =>
    t(`fuel.consumption.${key}`, options);
  const editor = source.scope === 'ship' ? source.consumptionEditor : undefined;
  const editable = !!editor && !!detail && detail.can.edit && detail.inLedger;
  const fieldError = (path: string) => {
    const code = issues[`data.consumption.${path}`];
    return code ? t(`fuel.fieldErrors.${code}`, { defaultValue: code }) : null;
  };
  const kindLabel = (kind: string) => labels.typeLabel(kind as VoyageReportKind);
  const update = (patch: Partial<FuelConsumptionSection>) => {
    setValues((current) => (current ? { ...current, ...patch } : current));
    setDirty(true);
    setDone(null);
  };
  const updateLine = (index: number, patch: Partial<FuelConsumptionLine>) =>
    update({ lines: lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) });
  const go = (path: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (shell) followLink(event, path, shell.navigate);
  };
  const tankName = (id: string | null) => {
    const tank = context.tanks.find((x) => x.id === id);
    return tank ? `${tank.code} (${fuelCode(tank.fuelTypeId)})` : '—';
  };
  const equipmentName = (id: string | null) => {
    const item = context.equipment.find((e) => e.id === id);
    return item ? `${item.code} (${item.name})` : '—';
  };

  if (list.items.length === 0 || !detail || !values) {
    return (
      <section className={`${classes.card} ${cx.empty}`} aria-label={t('fuel.tabs.consumption')}>
        <IconFileText size={36} className={classes.emptyIcon} aria-hidden />
        <h2 className={cx.emptyTitle}>{c('noReport')}</h2>
        <p className={classes.hint}>{c('noReportBody')}</p>
        {list.ledgerStart === null ? <p className={classes.hint}>{c('noLedger')}</p> : null}
        {source.scope === 'ship' ? (
          <Button
            component="a"
            href="/voyage/noon-report"
            onClick={go('/voyage/noon-report')}
            leftSection={<IconPlus size={16} />}
          >
            {c('openNext')}
          </Button>
        ) : null}
      </section>
    );
  }

  const start = utcParts(values.periodStart);
  const hours =
    values.periodStart && Date.parse(values.periodStart) < Date.parse(detail.periodEnd)
      ? consumptionDurationHours(values.periodStart, detail.periodEnd)
      : null;
  const line = lines[selected] ?? null;
  const fuelsInLines = [...new Set(lines.map((l) => l.fuelTypeId).filter((f) => f !== null))];
  const splits = values.splits ?? [];
  const showSplits =
    splits.length > 0 || (editable && detail.flags.some((f) => f.code === 'YEAR_SPLIT_MISSING'));

  const save = async (): Promise<FuelConsumptionDetail | null> => {
    if (!editor) return null;
    const saved = await editor.save(detail.reportId, detail.rowVersion, sectionInput(values));
    setDetail(saved);
    setDirty(false);
    return saved;
  };
  const run = async (action: 'save' | 'submit') => {
    if (!editor) return;
    setBusy(action);
    setError(null);
    setDone(null);
    setIssues({});
    try {
      if (action === 'save') {
        const saved = await save();
        if (saved) show(saved);
        setDone(c('saved'));
        await load(detail.reportId);
        setDone(c('saved'));
      } else {
        const current = dirty ? await save() : detail;
        if (!current) return;
        await editor.submit(current.reportId, current.contentHash);
        await load(current.reportId);
        setDone(c('submitted'));
      }
    } catch (failure) {
      setIssues(issuesOf(failure));
      setError(errorText(failure));
    } finally {
      setBusy(null);
    }
  };

  const statusNote =
    detail.status === 'void'
      ? c('voidNote')
      : !detail.inLedger
        ? list.ledgerStart === null
          ? c('noLedger')
          : c('notInLedger')
        : detail.status === 'posted'
          ? c('postedNote')
          : null;

  return (
    <div className={classes.alerts}>
      <section
        className={`${classes.card} ${cx.source}`}
        aria-label={c('sourceReport', { kind: kindLabel(detail.kind), no: detail.reportNo })}
      >
        <span className={cx.sourceIcon} aria-hidden>
          <IconFileText size={26} />
        </span>
        <div className={cx.sourceText}>
          <h2 className={cx.sourceTitle}>
            {c('sourceReport', { kind: kindLabel(detail.kind), no: detail.reportNo })}
            <span className={`${cx.status} ${cx[`status_${detail.status}`] ?? ''}`}>
              {c(`status.${detail.status}`)}
            </span>
          </h2>
          <p className={cx.sourceMeta}>
            {c('sourceMeta', {
              at: formatDateTime(detail.reportedAt, 0),
              vessel: list.vessel.name,
            })}
          </p>
        </div>
        <div className={cx.sourceActions}>
          <Select
            className={cx.reportSelect}
            aria-label={c('report')}
            value={detail.reportId}
            allowDeselect={false}
            data={list.items.map((item) => ({
              value: item.reportId,
              label: c('reportOption', {
                no: item.reportNo,
                kind: kindLabel(item.kind),
                status: c(`status.${item.status}`),
              }),
            }))}
            onChange={(value) => {
              if (value && value !== detail.reportId && source.consumption) {
                void source.consumption(vesselId, value).then(show, (failure) => {
                  setError(errorText(failure));
                });
              }
            }}
          />
          <Button
            component="a"
            variant="outline"
            href={sourceReportPath(detail, source.scope === 'web' ? list.vessel.id : null)}
            onClick={go(sourceReportPath(detail, source.scope === 'web' ? list.vessel.id : null))}
            leftSection={<IconExternalLink size={18} />}
          >
            {c('openReport')}
          </Button>
        </div>
      </section>

      {source.scope === 'web' ? (
        <Alert color="blue" role="note">
          {c('officeReadOnly')}
        </Alert>
      ) : null}
      {statusNote ? (
        <Alert color={detail.status === 'void' ? 'red' : 'blue'} role="note">
          {statusNote}
        </Alert>
      ) : null}
      {detail.flags.map((flag) => (
        <Alert
          key={`${flag.code}-${flag.lineKey ?? ''}`}
          color="yellow"
          icon={<IconAlertTriangle size={18} />}
          role="status"
        >
          {c(`flags.${flag.code}`, {
            line: lines.findIndex((l) => l.key === flag.lineKey) + 1,
          })}
        </Alert>
      ))}
      <Notices error={error} done={done} />

      <section className={classes.card} aria-label={c('info')}>
        <div className={classes.head}>
          <h2 className={classes.title}>{c('info')}</h2>
        </div>
        <div className={classes.body}>
          <div className={cx.infoTop}>
            <DateTime
              label={c('periodStart')}
              day={start.day}
              time={start.time}
              readOnly={!editable}
              onDay={(day) => update({ periodStart: utcOf(day, start.time ?? '00:00') })}
              onTime={(time) => update({ periodStart: utcOf(start.day, time) })}
              error={fieldError('periodStart')}
            />
            <DateTime
              label={c('periodEnd')}
              day={utcParts(detail.periodEnd).day}
              time={utcParts(detail.periodEnd).time}
              readOnly
              onDay={() => {}}
              onTime={() => {}}
            />
            <TextInput
              className={classes.computed}
              label={c('duration')}
              readOnly
              value={hours === null ? '—' : c('hours', { value: Math.round(hours * 100) / 100 })}
            />
          </div>
          {editable &&
          detail.suggestedPeriodStart &&
          detail.suggestedPeriodStart !== values.periodStart ? (
            <p className={classes.hint}>
              {c('suggested', { at: formatDateTime(detail.suggestedPeriodStart, 0) })}{' '}
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => update({ periodStart: detail.suggestedPeriodStart })}
              >
                {c('useSuggested')}
              </Button>
            </p>
          ) : null}
          <div className={cx.infoBottom}>
            <Select
              label={c('operation')}
              withAsterisk
              readOnly={!editable}
              value={values.operation}
              data={OPERATIONS.map((value) => ({ value, label: c(`operations.${value}`) }))}
              onChange={(value) => update({ operation: value })}
              error={fieldError('operation')}
            />
            <TextInput
              label={c('voyage')}
              readOnly
              className={classes.computed}
              value={detail.voyageNo ?? c('unassigned')}
              description={detail.unassigned ? c('unassignedHint') : undefined}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            />
            <TextInput
              label={c('leg')}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              readOnly
              className={classes.computed}
              value={
                detail.leg !== null && detail.leg !== undefined
                  ? String(detail.leg).padStart(2, '0')
                  : c('noLeg')
              }
              description={c('derivedLater')}
            />
            <TextInput
              label={c('cp')}
              readOnly
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              className={classes.computed}
              value={detail.charterParty ? (detail.charterParty.cpNo ?? '—') : c('noCp')}
              description={
                detail.charterParty
                  ? c(`cpCoverage.${detail.charterParty.coverage}`)
                  : c('derivedLater')
              }
            />
          </div>
          <p className={classes.hint}>{c('utcNote')}</p>
        </div>
      </section>

      <section className={classes.card} aria-label={c('details')}>
        <div className={classes.head}>
          <h2 className={classes.title}>{c('details')}</h2>
          <div className={cx.total}>
            <span>{c('total')}</span>
            <strong className={cx.totalValue}>{mt(total)}</strong>
          </div>
        </div>
        <div
          className={classes.tableWrap}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${classes.table} ${cx.lines}`}>
            <thead>
              <tr>
                <th>{c('columns.no')}</th>
                <th>{c('columns.equipment')} *</th>
                <th>{c('columns.fuel')} *</th>
                <th>{c('columns.tank')} *</th>
                <th className={classes.num}>{c('columns.runningHours')}</th>
                <th>{c('columns.method')} *</th>
                <th className={classes.num}>{c('columns.mass')} *</th>
                <th>{c('columns.quality')} *</th>
                {editable ? <th className={classes.center}>{c('columns.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={editable ? 9 : 8}>{c('noLines')}</td>
                </tr>
              ) : (
                lines.map((row, index) => {
                  const no = index + 1;
                  const path = (field: string) => `lines.${index}.${field}`;
                  const label = (column: string) => `${c(`columns.${column}`)} ${no}`;
                  return (
                    <tr
                      key={row.key}
                      className={index === selected ? cx.selectedRow : undefined}
                      onClick={() => setSelected(index)}
                    >
                      <td>{no}</td>
                      <td>
                        {editable ? (
                          <Select
                            size="sm"
                            aria-label={label('equipment')}
                            value={row.equipmentId}
                            data={context.equipment.map((e) => ({
                              value: e.id,
                              label: `${e.code} (${e.name})`,
                            }))}
                            searchable
                            onChange={(value) => updateLine(index, { equipmentId: value })}
                            error={fieldError(path('equipmentId'))}
                          />
                        ) : (
                          equipmentName(row.equipmentId)
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <Select
                            size="sm"
                            aria-label={label('fuel')}
                            value={row.fuelTypeId}
                            data={context.fuelTypes.map((f) => ({ value: f.id, label: f.code }))}
                            onChange={(value) => updateLine(index, { fuelTypeId: value })}
                            error={fieldError(path('fuelTypeId'))}
                          />
                        ) : (
                          fuelCode(row.fuelTypeId)
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <Select
                            size="sm"
                            aria-label={label('tank')}
                            value={row.tankId}
                            data={context.tanks
                              .filter(
                                (tank) =>
                                  !row.fuelTypeId ||
                                  tank.allowedFuelTypeIds.length === 0 ||
                                  tank.allowedFuelTypeIds.includes(row.fuelTypeId),
                              )
                              .map((tank) => ({ value: tank.id, label: tankName(tank.id) }))}
                            onChange={(value) => updateLine(index, { tankId: value })}
                            error={fieldError(path('tankId'))}
                          />
                        ) : (
                          tankName(row.tankId)
                        )}
                      </td>
                      <td className={classes.num}>
                        {editable ? (
                          <TextInput
                            size="sm"
                            inputMode="decimal"
                            aria-label={label('runningHours')}
                            value={row.runningHours ?? ''}
                            onChange={(e) =>
                              updateLine(index, {
                                runningHours: e.currentTarget.value.replace(',', '.'),
                              })
                            }
                            error={fieldError(path('runningHours'))}
                          />
                        ) : (
                          mt(row.runningHours, 1)
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <Select
                            size="sm"
                            aria-label={label('method')}
                            value={row.method}
                            data={METHODS.map((value) => ({
                              value,
                              label: c(`methods.${value}`),
                            }))}
                            onChange={(value) => updateLine(index, { method: value })}
                            error={fieldError(path('method'))}
                          />
                        ) : row.method ? (
                          c(`methods.${row.method}`)
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={classes.num}>
                        {editable ? (
                          <TextInput
                            size="sm"
                            inputMode="decimal"
                            aria-label={label('mass')}
                            value={row.massMt ?? ''}
                            onChange={(e) =>
                              updateLine(index, { massMt: e.currentTarget.value.replace(',', '.') })
                            }
                            error={fieldError(path('massMt'))}
                          />
                        ) : (
                          mt(row.massMt)
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <Select
                            size="sm"
                            aria-label={label('quality')}
                            value={row.quality}
                            data={QUALITIES.map((value) => ({
                              value,
                              label: c(`qualities.${value}`),
                            }))}
                            onChange={(value) => updateLine(index, { quality: value })}
                            error={fieldError(path('quality'))}
                          />
                        ) : row.quality ? (
                          <span className={`${cx.quality} ${cx[`quality_${row.quality}`] ?? ''}`}>
                            {c(`qualities.${row.quality}`)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      {editable ? (
                        <td className={classes.center}>
                          <div className={cx.rowActions}>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={c('editLine', { no })}
                              onClick={() => {
                                setSelected(index);
                                setOpen(true);
                              }}
                            >
                              <IconPencil size={18} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={c('removeLine', { no })}
                              onClick={(event) => {
                                event.stopPropagation();
                                update({ lines: lines.filter((_, i) => i !== index) });
                                setSelected(0);
                              }}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {line ? (
          <div className={cx.selected}>
            <div className={cx.selectedHead}>
              <h3 className={cx.selectedTitle}>
                {c('selected', { name: equipmentName(line.equipmentId) })}
              </h3>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={c('toggleSelected')}
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
              >
                {open ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
              </ActionIcon>
            </div>
            {open ? (
              <LineDetails
                line={line}
                index={selected}
                editable={editable}
                context={context}
                fieldError={fieldError}
                onChange={(patch) => updateLine(selected, patch)}
              />
            ) : null}
          </div>
        ) : null}
      </section>

      {showSplits ? (
        <section className={classes.card} aria-label={c('splits')}>
          <div className={classes.body}>
            <Section
              icon={<IconFlame size={18} />}
              title={c('splits')}
              action={
                editable ? (
                  <Button
                    variant="outline"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() =>
                      update({
                        splits: [
                          ...splits,
                          {
                            at: null,
                            reason: 'year',
                            basis: null,
                            lines: fuelsInLines.map((fuelTypeId) => ({ fuelTypeId, massMt: null })),
                          },
                        ],
                      })
                    }
                  >
                    {c('addSplit')}
                  </Button>
                ) : undefined
              }
            >
              <p className={classes.hint}>{c('splitsHint')}</p>
              {splits.map((split, index) => (
                <SplitRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: ayrımın kimliği yok, sıra kimliktir
                  key={index}
                  split={split}
                  index={index}
                  editable={editable}
                  fuelCode={fuelCode}
                  fieldError={fieldError}
                  onChange={(next) =>
                    update({ splits: splits.map((s, i) => (i === index ? next : s)) })
                  }
                  onRemove={() => update({ splits: splits.filter((_, i) => i !== index) })}
                />
              ))}
            </Section>
          </div>
        </section>
      ) : null}

      {editable ? (
        <div className={cx.footer}>
          <Button
            variant="outline"
            className={classes.footerStart}
            leftSection={<IconPlus size={18} />}
            onClick={() => {
              update({ lines: [...lines, emptyLine(`l${crypto.randomUUID().slice(0, 8)}`)] });
              setSelected(lines.length);
              setOpen(true);
            }}
          >
            {c('addLine')}
          </Button>
          <Button
            variant="default"
            leftSection={<IconDeviceFloppy size={18} />}
            loading={busy === 'save'}
            disabled={busy !== null}
            onClick={() => void run('save')}
          >
            {c('saveDraft')}
          </Button>
          {detail.can.submit ? (
            <Button
              leftSection={<IconSend size={18} />}
              loading={busy === 'submit'}
              disabled={busy !== null}
              onClick={() => void run('submit')}
            >
              {c('submit')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Seçili satırın ayrıntısı: sayaç, birim, hesap dayanağı, açıklama; parti, yoğunluk, sıfırlama. */
function LineDetails({
  line,
  index,
  editable,
  context,
  fieldError,
  onChange,
}: {
  line: FuelConsumptionLine;
  index: number;
  editable: boolean;
  context: FuelContextData;
  fieldError: (path: string) => string | null;
  onChange: (patch: Partial<FuelConsumptionLine>) => void;
}) {
  const { t } = useTranslation();
  const c = (key: string) => t(`fuel.consumption.${key}`);
  const meter = line.meter ?? blankMeter();
  const setMeter = (patch: Partial<typeof meter>) => onChange({ meter: { ...meter, ...patch } });
  const path = (field: string) => `lines.${index}.${field}`;
  const reset = meter.reset;
  const resetParts = utcParts(reset?.at ?? null);
  const decimal = (value: string) => value.replace(',', '.');
  return (
    <div className={cx.detailGrid}>
      <TextInput
        label={c('meterStart')}
        inputMode="decimal"
        readOnly={!editable}
        value={meter.start ?? ''}
        onChange={(e) => setMeter({ start: decimal(e.currentTarget.value) })}
        error={fieldError(path('meter.start'))}
      />
      <TextInput
        label={c('meterEnd')}
        inputMode="decimal"
        readOnly={!editable}
        value={meter.end ?? ''}
        onChange={(e) => setMeter({ end: decimal(e.currentTarget.value) })}
        error={fieldError(path('meter.end'))}
      />
      <Select
        label={c('meterUnit')}
        readOnly={!editable}
        value={meter.unit}
        data={UNITS.map((value) => ({ value, label: t(`fuel.consumption.units.${value}`) }))}
        onChange={(value) => setMeter({ unit: value })}
      />
      <TextInput
        label={c('basis')}
        withAsterisk={line.quality !== 'measured'}
        readOnly={!editable}
        placeholder={editable ? c('basisPlaceholder') : undefined}
        value={line.basis ?? ''}
        onChange={(e) => onChange({ basis: e.currentTarget.value })}
        error={fieldError(path('basis'))}
      />
      <TextInput
        className={cx.wide}
        label={c('remarks')}
        readOnly={!editable}
        value={line.remarks ?? ''}
        onChange={(e) => onChange({ remarks: e.currentTarget.value })}
        error={fieldError(path('remarks'))}
      />
      <TextInput
        label={c('deviceId')}
        readOnly={!editable}
        value={meter.deviceId ?? ''}
        onChange={(e) => setMeter({ deviceId: e.currentTarget.value })}
      />
      <Select
        label={c('batch')}
        readOnly={!editable}
        clearable={editable}
        value={line.batchId}
        data={context.bunkerings
          .filter((b) => !line.fuelTypeId || b.fuelTypeId === line.fuelTypeId)
          .map((b) => ({
            value: b.id,
            label: b.bdnNo ? `${b.recordNo} · ${b.bdnNo}` : b.recordNo,
          }))}
        onChange={(value) => onChange({ batchId: value })}
        error={fieldError(path('batchId'))}
      />
      <TextInput
        label={c('density')}
        inputMode="decimal"
        readOnly={!editable}
        value={line.density15 ?? ''}
        onChange={(e) => onChange({ density15: decimal(e.currentTarget.value) })}
        error={fieldError(path('density15'))}
      />
      <Checkbox
        className={cx.resetToggle}
        label={c('reset')}
        disabled={!editable}
        checked={reset !== null}
        onChange={(e) =>
          setMeter({ reset: e.currentTarget.checked ? { at: null, rolloverAt: null } : null })
        }
      />
      {reset ? (
        <>
          <DateTime
            label={c('resetAt')}
            day={resetParts.day}
            time={resetParts.time}
            readOnly={!editable}
            onDay={(day) =>
              setMeter({ reset: { ...reset, at: utcOf(day, resetParts.time ?? '00:00') } })
            }
            onTime={(time) => setMeter({ reset: { ...reset, at: utcOf(resetParts.day, time) } })}
            error={fieldError(path('meter.reset.at'))}
          />
          <TextInput
            label={c('rolloverAt')}
            inputMode="decimal"
            readOnly={!editable}
            value={reset.rolloverAt ?? ''}
            onChange={(e) =>
              setMeter({ reset: { ...reset, rolloverAt: decimal(e.currentTarget.value) } })
            }
            error={fieldError(path('meter.reset.rolloverAt'))}
          />
        </>
      ) : null}
    </div>
  );
}

/** Dönem ayrımı: sınır anı (UTC), neden, dayanak ve yakıt başına sınırdan önceki tüketim. */
function SplitRow({
  split,
  index,
  editable,
  fuelCode,
  fieldError,
  onChange,
  onRemove,
}: {
  split: FuelConsumptionSplit;
  index: number;
  editable: boolean;
  fuelCode: (id: string | null) => string;
  fieldError: (path: string) => string | null;
  onChange: (next: FuelConsumptionSplit) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const c = (key: string, options: Record<string, unknown> = {}) =>
    t(`fuel.consumption.${key}`, options);
  const at = utcParts(split.at);
  const path = (field: string) => `splits.${index}.${field}`;
  return (
    <div className={cx.split}>
      <DateTime
        label={c('splitAt')}
        day={at.day}
        time={at.time}
        readOnly={!editable}
        onDay={(day) => onChange({ ...split, at: utcOf(day, at.time ?? '00:00') })}
        onTime={(time) => onChange({ ...split, at: utcOf(at.day, time) })}
        error={fieldError(path('at'))}
      />
      <Select
        label={c('splitReason')}
        withAsterisk
        readOnly={!editable}
        value={split.reason}
        data={SPLIT_REASONS.map((value) => ({ value, label: c(`splitReasons.${value}`) }))}
        onChange={(value) => onChange({ ...split, reason: value })}
        error={fieldError(path('reason'))}
      />
      <TextInput
        label={c('splitBasis')}
        withAsterisk
        readOnly={!editable}
        value={split.basis ?? ''}
        onChange={(e) => onChange({ ...split, basis: e.currentTarget.value })}
        error={fieldError(path('basis'))}
      />
      {split.lines.map((part, partIndex) => (
        <TextInput
          key={part.fuelTypeId ?? partIndex}
          label={c('splitBefore', { fuel: fuelCode(part.fuelTypeId) })}
          inputMode="decimal"
          readOnly={!editable}
          value={part.massMt ?? ''}
          onChange={(e) =>
            onChange({
              ...split,
              lines: split.lines.map((p, i) =>
                i === partIndex ? { ...p, massMt: e.currentTarget.value.replace(',', '.') } : p,
              ),
            })
          }
          error={fieldError(path(`lines.${partIndex}.massMt`))}
        />
      ))}
      {editable ? (
        <ActionIcon
          className={cx.splitRemove}
          variant="subtle"
          color="red"
          aria-label={c('removeSplit', { no: index + 1 })}
          onClick={onRemove}
        >
          <IconTrash size={18} />
        </ActionIcon>
      ) : null}
    </div>
  );
}
